import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import GoogleSignInButton from "@/components/GoogleSignInButton";

const BOOK_DEMO_URL = "mailto:contact@push2stream.com?subject=MOXYPLAY%20demo%20request";

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/console/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground brand-hero-bg grain relative overflow-hidden flex flex-col">
      {/* Aurora blobs */}
      <div className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(139,92,246,0.6), transparent 65%)" }} />
      <div className="absolute -bottom-40 -right-40 w-[620px] h-[620px] rounded-full opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(34,211,238,0.5), transparent 65%)" }} />
      <div className="absolute top-1/3 right-1/3 w-[400px] h-[400px] rounded-full opacity-20 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,47,184,0.5), transparent 65%)" }} />

      <div className="relative px-6 py-4 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="login-back-home">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg overflow-hidden p-1 flex items-center justify-center" style={{ background: "var(--brand-bg-deep)", boxShadow: "0 0 12px rgba(139,92,246,0.35)" }}>
            <img src="https://customer-assets-jt897jd0.emergentagent.net/job_broadcast-cloud-2/artifacts/jeb6uhlp_FullLogo_Transparent_NoBuffer.png" alt="MOXYPLAY" className="w-full h-full object-contain" />
          </div>
          <div className="font-display font-black text-sm text-gradient tracking-tight">MOXYPLAY</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12 relative">
        <div className="w-full max-w-md glass-card p-8 sm:p-10" data-testid="login-glass-card">
          <div className="inline-flex items-center gap-2 gradient-stroke rounded-full px-3 py-1 text-[10px] uppercase tracking-widest font-mono text-foreground/80 mb-6">
            <span className="dot-live" /> Console access
          </div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter">Sign in.</h1>
          <p className="text-muted-foreground text-sm mt-2">Enter your credentials to access the MOXYPLAY console.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground" htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 glass-input rounded-full h-11 px-4 font-mono text-sm"
                required
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground" htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 glass-input rounded-full h-11 px-4 font-mono text-sm"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-btn"
              className="w-full tenant-btn rounded-full h-11"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enter console →"}
            </Button>
          </form>

          <div className="mt-4 flex items-center gap-3">
            <div className="flex-1 h-px bg-border" />
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">or</div>
            <div className="flex-1 h-px bg-border" />
          </div>
          <div className="mt-4">
            <GoogleSignInButton testId="login-google-btn" label="Continue with Google" />
          </div>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            Need access?{" "}
            <a href={BOOK_DEMO_URL} className="text-foreground underline hover:text-cyan-400 transition-colors" data-testid="login-book-demo-link">
              Book a demo
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
